import type { DesignHierarchyNode } from '@cadstrom/sigma-engine-shared';

/** A flattened hierarchy node annotated with tree display info */
export interface FlatNode {
	/** Unique key for this node in the tree (designId + instanceId or index-based) */
	key: string;
	/** Absent for folder nodes that cannot be rendered */
	designId?: string;
	instanceId?: string;
	label: string;
	/** Full path from root to this node (e.g. "Root > Parent > Child") */
	fullPath: string;
	depth: number;
	hasChildren: boolean;
	/** True if this is a folder node with no designId */
	isFolder: boolean;
	/** Parent key for expand/collapse tracking */
	parentKey?: string;
	/**
	 * Present only during search mode:
	 * - 'match': label matches the query
	 * - 'ancestor': included because a descendant matches (shown muted)
	 * - 'descendant': included because an ancestor matches
	 */
	matchType?: 'match' | 'ancestor' | 'descendant';
}

export type MatchTag = 'match' | 'ancestor' | 'descendant';

/**
 * Returns true if a node should default to collapsed.
 * A node defaults to collapsed only when all of its children share the same designId as the parent.
 * (e.g., multi-instance children that are just different instances of the same sheet.)
 */
export function shouldDefaultCollapse(node: DesignHierarchyNode): boolean {
	if (!node.children || node.children.length === 0 || node.designId === undefined) {
		return false;
	}
	const parentDesignId = node.designId;
	return node.children.every((child) => child.designId === parentDesignId);
}

/**
 * Flatten a hierarchy tree into an ordered list of FlatNodes.
 * `toggledKeys` contains keys whose expand/collapse state has been flipped from default.
 */
export function flattenHierarchy(
	nodes: DesignHierarchyNode[],
	depth: number,
	parentKey: string | undefined,
	parentPath: string | undefined,
	toggledKeys: Set<string>,
): FlatNode[] {
	const result: FlatNode[] = [];
	for (const [i, node] of nodes.entries()) {
		const key = parentKey ? `${parentKey}/${i}` : `${i}`;
		const fullPath = parentPath ? `${parentPath} > ${node.label}` : node.label;
		const hasChildren = (node.children?.length ?? 0) > 0;
		const isFolder = !node.designId;

		result.push({
			key,
			designId: node.designId,
			instanceId: node.instanceId,
			label: node.label,
			fullPath,
			depth,
			hasChildren,
			isFolder,
			parentKey,
		});

		if (hasChildren && node.children) {
			// Folders are always expanded (they're organizational containers, not toggleable).
			// For other nodes, toggling flips the default: most nodes default to expanded,
			// except multi-instance nodes (all children share parent's designId) which default to collapsed.
			const isExpanded =
				isFolder || (toggledKeys.has(key) ? shouldDefaultCollapse(node) : !shouldDefaultCollapse(node));

			if (isExpanded) {
				result.push(...flattenHierarchy(node.children, depth + 1, key, fullPath, toggledKeys));
			}
		}
	}
	return result;
}

/**
 * Tag each node in the hierarchy with how it relates to the search query.
 * Returns true if this node or any descendant matches.
 */
export function tagSearchMatches(
	nodes: DesignHierarchyNode[],
	query: string,
	tags: Map<DesignHierarchyNode, MatchTag>,
	ancestorMatched: boolean,
): boolean {
	let anyMatch = false;
	for (const node of nodes) {
		const selfMatches = node.label.toLowerCase().includes(query);
		const childrenHaveMatch =
			node.children !== undefined && node.children.length > 0 ?
				tagSearchMatches(node.children, query, tags, ancestorMatched || selfMatches)
			:	false;

		if (selfMatches) {
			tags.set(node, 'match');
			anyMatch = true;
		} else if (childrenHaveMatch) {
			tags.set(node, 'ancestor');
			anyMatch = true;
		} else if (ancestorMatched) {
			tags.set(node, 'descendant');
		}
	}
	return anyMatch;
}

/** Flatten a tagged hierarchy for search, preserving tree structure and depth. */
export function flattenSearchTree(
	nodes: DesignHierarchyNode[],
	tags: Map<DesignHierarchyNode, MatchTag>,
	depth: number,
	parentKey: string | undefined,
	parentPath: string | undefined,
): FlatNode[] {
	const result: FlatNode[] = [];
	for (const [i, node] of nodes.entries()) {
		const tag = tags.get(node);
		if (tag === undefined) {
			continue;
		}

		const key = parentKey ? `${parentKey}/${i}` : `${i}`;
		const fullPath = parentPath ? `${parentPath} > ${node.label}` : node.label;
		const hasChildren = (node.children?.length ?? 0) > 0;

		result.push({
			key,
			designId: node.designId,
			instanceId: node.instanceId,
			label: node.label,
			fullPath,
			depth,
			hasChildren,
			isFolder: !node.designId,
			parentKey,
			matchType: tag,
		});

		if (hasChildren && node.children) {
			result.push(...flattenSearchTree(node.children, tags, depth + 1, key, fullPath));
		}
	}
	return result;
}

/** Flatten entire hierarchy unconditionally (for selectedNode lookup). */
export function flattenAll(
	nodes: DesignHierarchyNode[],
	depth: number,
	parentKey: string | undefined,
	parentPath: string | undefined,
): FlatNode[] {
	const result: FlatNode[] = [];
	for (const [i, node] of nodes.entries()) {
		const key = parentKey ? `${parentKey}/${i}` : `${i}`;
		const fullPath = parentPath ? `${parentPath} > ${node.label}` : node.label;
		result.push({
			key,
			designId: node.designId,
			instanceId: node.instanceId,
			label: node.label,
			fullPath,
			depth,
			hasChildren: (node.children?.length ?? 0) > 0,
			isFolder: !node.designId,
			parentKey,
		});
		if (node.children) {
			result.push(...flattenAll(node.children, depth + 1, key, fullPath));
		}
	}
	return result;
}
